import { z } from 'zod';

const nameField = z.string().trim().min(1).max(240);

export const archiveZipInput = z.object({
  ids: z.array(z.uuid()).min(1).max(5000),
  name: nameField.optional(),
  /** Destination folder; null is the root. Defaults to the folder of the first item. */
  parentId: z.uuid().nullable().optional(),
});
export type ArchiveZipInput = z.infer<typeof archiveZipInput>;

export const archiveDownloadInput = z.object({
  ids: z.array(z.uuid()).min(1).max(5000),
  name: nameField.optional(),
});

export const archiveExtractInput = z.object({
  id: z.uuid(),
  /** "here" puts the contents next to the archive; "folder" wraps them in a folder named after it. */
  mode: z.enum(['here', 'folder']),
  /** Listing indexes to extract (folders imply their contents); everything when omitted. */
  entries: z.array(z.number().int().min(0)).max(500_000).optional(),
});
export type ArchiveExtractInput = z.infer<typeof archiveExtractInput>;

/** Extensions the server can open (ZIP in-process, the rest through 7-Zip). */
export const EXTRACTABLE_EXTENSIONS = ['zip', 'rar', '7z', 'tar', 'tgz', 'gz', 'bz2', 'xz', 'tbz', 'tbz2', 'txz'] as const;

/** True for names the archive tools accept, e.g. "backup.tar.gz". */
export function isArchiveName(name: string): boolean {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return false;
  return (EXTRACTABLE_EXTENSIONS as readonly string[]).includes(name.slice(dot + 1).toLowerCase());
}

export const ARCHIVE_JOB_KINDS = ['zip', 'extract'] as const;
export type ArchiveJobKind = (typeof ARCHIVE_JOB_KINDS)[number];

/** Stable failure codes; the UI turns them into sentences. */
export const ARCHIVE_ERRORS = [
  'archive_encrypted',
  'archive_invalid',
  'archive_unsupported',
  'archive_too_many_entries',
  'archive_too_large',
  'archive_suspicious',
  'archive_empty',
  'cancelled',
  'interrupted',
  'tool_missing',
  'source_missing',
  'storage_full',
  'not_found',
  'timeout',
  'archive_failed',
] as const;
export type ArchiveError = (typeof ARCHIVE_ERRORS)[number];

export interface ArchiveJobDetail {
  files: number;
  folders: number;
  /** Symlinks, devices and unsafe or too-deep paths that were left out. */
  skipped: number;
  bytes: number;
  /** Top-level items the job created, so the UI can select them. */
  createdIds: string[];
}

export interface ArchiveJob {
  id: string;
  kind: ArchiveJobKind;
  status: 'queued' | 'running' | 'done' | 'failed';
  /** 0–1. */
  progress: number;
  resultFileId: string | null;
  error: ArchiveError | null;
  detail: ArchiveJobDetail | null;
  createdAt: string;
}

export interface ArchiveEntry {
  /** Position in the listing; extraction refers to entries by it. */
  index: number;
  /** Safe relative path with "/" separators, after name cleaning and de-duplication. */
  path: string;
  dir: boolean;
  size: number;
  modified: string | null;
  encrypted: boolean;
  /** Left out on extraction (link, device, unsafe path); shown greyed out. */
  skipped: boolean;
}

export interface ArchiveListing {
  format: string;
  entries: ArchiveEntry[];
  encrypted: boolean;
  skipped: number;
  totalSize: number;
}

export interface ArchiveDownloadLink {
  url: string;
  name: string;
  expiresAt: string;
}
